import { ReactElement, useEffect, useState } from 'react'
import { ContextProps, createContext } from '../common/contextHelper'
import { UnitConversions, UserUnitSettings } from '../common/constants'
import { api } from '../services'
import { Devices } from '../common/enums'
import { UNIT_CONVERSIONS } from '../common/unitConversions'
import { useAuthContext } from './AuthContext'
import { useAppContext } from './AppContext'

interface UserContextState {
  isLoading: boolean
  devices: Devices
  selectedDevice: string
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
  setUserUnitSettings: (userUnitSettings: UserUnitSettings) => void
  selectDevice: (sigfoxId: string) => void
  clearDevices: () => void
  selectDeviceLocally: (sigfoxId: string) => void
}

const [Provider, useAccountContext] = createContext<UserContextState>(module.filename)

export { useAccountContext }

export function AccountProvider({ children }: ContextProps): ReactElement {
  const { email } = useAuthContext()
  const { showCharts, setShowCharts } = useAppContext()

  const [devices, setDevices] = useState<Devices>({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState('')
  const [userUnitSettings, setUserUnitSettings] = useState<UserUnitSettings>({})
  const unitConversions = UNIT_CONVERSIONS

  useEffect(() => {
    if (email) {
      sync()

      return () => {
        setIsLoading(false)
      }
    } else {
      setDevices({})
      setShowCharts(false)
      setSelectedDevice('')
    }

    async function sync() {
      // TODO: need to make sure that we have valid API token here before calling this
      try {
        if (email) {
          setIsLoading(true)
          syncUserUnitSettings()
          const result = await api.getDevices(true, true)
          setDevices(
            result.devices.reduce((acc: Devices, device: any) => {
              acc[device.sigfoxId] = device
              return acc
            }, {} as Devices)
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [email]) // eslint-disable-line react-hooks/exhaustive-deps

  function selectDevice(sigfoxId: string) {
    setSelectedDevice(sigfoxId)
  }

  async function syncUserUnitSettings(): Promise<UserUnitSettings> {
    const result = await api.getUserUnits()
    setUserUnitSettings(result.data.userUnitSettings)
    return result.data.userUnitSettings
  }

  function selectDeviceLocally(sigfoxId: string) {
    // worker.sendRequest('test', { test: 'test' })
    if (sigfoxId === selectedDevice && sigfoxId !== '') {
      if (showCharts) {
        selectDevice('')
      }
      setShowCharts(!showCharts)
    } else {
      selectDevice(sigfoxId)
    }
  }

  return (
    <Provider
      value={{
        isLoading,
        devices,
        selectedDevice,
        userUnitSettings,
        unitConversions,
        setUserUnitSettings,
        selectDevice,
        clearDevices: () => setDevices({}),
        selectDeviceLocally,
      }}
    >
      {children}
    </Provider>
  )
}
